import { call, put, takeLatest, select, delay } from 'redux-saga/effects';
import { capitalize, cloneDeep, get, isEqual, merge, set, update, uniqBy, findIndex } from 'lodash';

import { Trip, tripDetailsType } from 'entities/Trip';
import { tripCommonTripSelector, visitorSelector } from 'modules/common/CommonTrip/selectors';
import { FillFieldsService } from '../../../entities/FillBtn';
import { PhoneService } from '../../../entities/Phone/service';
import { OrderService } from '../../../entities/Order/service';
import { Document } from '../../../entities/Document/interfaces';
import {
  GET_ORDER_WEIGHT_REQUEST,
  GET_TRIP_FIELDS_REQUEST,
  GET_TRIP_ORDERS_REQUEST,
  GET_ORDER_PART_NUM_REQUEST,
} from './constants';
import {
  getTripFieldsRequest,
  getTripFieldsSuccess,
  getTripFieldsError,
  getOrderWeightRequest,
  getOrderWeightSuccess,
  getTripOrdersRequest,
  getOrderPartNumRequest,
  getTripOrdersSuccess,
  getTripOrdersError,
  clearError,
  getOrderWeightError,
} from './actions';
import {
  deleteOrderFromTripRequest,
  deleteOrderFromTripSuccess,
  getNewTripSuccess,
  hasWarning,
  setVisitorName,
} from '../CommonTrip/actions';
import { dataDocsSelector } from '../CommonTrip/selectors';
import { objToFormData } from '../../../utils';
import { DELETE_ORDER_FROM_TRIP_REQUEST } from '../CommonTrip/constants';

function* getTripFieldsSaga(action: ReturnType<typeof getTripFieldsRequest>) {
  const { fieldName, paramName, value, tripValue } = action.payload;
  let url = '';
  const type = fieldName.split('.')[0] as tripDetailsType;
  let params = { [paramName]: paramName === 'phoneNumber' ? value.replace(/\D+/g, '') : value };
  if (['driver', 'forwarder'].includes(type)) {
    url = `/${capitalize(type)}`;
  } else if ((type === 'vehicle' || type === 'trailer') && paramName.includes('licensePlate')) {
    url = `/Transport/LicensePlate`;
    params = {
      number: value,
      type: type === 'vehicle' ? '1' : '2',
    };
  } else if (type === 'vehicle') {
    url = '/Transport';
  } else {
    url = '/Orders/Weight';
  }

  const loaded = {
    type: fieldName,
    value,
    isError: false,
  };

  try {
    // @ts-ignore
    const data = yield call(FillFieldsService.getFields, url, { ...params, branchId: tripValue.branchId });

    if (url === '/Forwarder') {
      const idx = findIndex(data.documents, (doc: any) => doc.type === 1);
      data.documents.splice(idx, 1);
    }
    const trip: Trip = yield select(tripCommonTripSelector);
    const dataDocs: Document[] = yield select(dataDocsSelector);
    const dataVisitor: any[] = yield select(visitorSelector);

    const phone = get(data, 'phone.number');

    if (phone) {
      set(data, 'phone.number', PhoneService.formatPhone(phone));
    }

    if (isEqual(type, 'driver') && (!data?.documents || !data.documents.length)) {
      const driverDocs = [
        {
          type: 1,
          number: '',
          pictures: [],
          pictureFiles: [],
        },
        {
          type: 3,
          number: '',
          pictures: [],
          pictureFiles: [],
        },
      ];
      set(data, 'documents', driverDocs);
    }

    if (isEqual(type, 'vehicle') || isEqual(type, 'trailer')) {
      if (!data) {
        set(data, 'licensePlate', '');
      }
      if (!data?.document) {
        set(data, 'document', {
          type: 2,
          number: '',
          pictures: [],
          pictureFiles: [],
        });
      }
    }

    if (isEqual(type, 'driver') && isEqual(data?.documents?.length, 1)) {
      const driverDocs = cloneDeep(data.documents);
      driverDocs.push({
        type: isEqual(data.documents[0].type, 1) ? 3 : 1,
        number: '',
        pictures: [],
        pictureFiles: [],
      });
      set(data, 'documents', driverDocs);
    }

    const docsDatas =
      isEqual(type, 'driver') || isEqual(type, 'forwarder') ? get(data, 'documents') : [get(data, 'document')];
    let updatedDocs: Document[] = [];

    if (docsDatas) {
      const mergeddocs = cloneDeep(docsDatas);
      mergeddocs.forEach((doc: any) => set(doc, 'fieldName', type));

      updatedDocs = [...dataDocs.filter((d: any) => d.fieldName !== type), ...mergeddocs];
    } else {
      const mergeddocs = cloneDeep(data);
      set(mergeddocs, 'fieldName', type);
      updatedDocs = [...dataDocs.filter((d: any) => d.fieldName !== type), mergeddocs];
    }

    if (isEqual(type, 'driver') || isEqual(type, 'forwarder')) {
      const visitor = {
        fieldName: type,
        firstName: cloneDeep(get(data, 'firstName', '')),
        middleName: cloneDeep(get(data, 'middleName', '')),
        lastName: cloneDeep(get(data, 'lastName', '')),
        phone: cloneDeep(get(data, 'phone', { id: '', number: '' })),
        id: cloneDeep(get(data, 'id', '')),
      };

      const visitors = dataVisitor.filter((f: any) => !isEqual(f.fieldName, type));

      yield put(setVisitorName([...visitors, visitor]));
    } else {
      const visitor = {
        fieldName: type,
        licensePlate: cloneDeep(get(data, 'licensePlate', '')),
        id: cloneDeep(get(data, 'id', '')),
      };

      const visitors = dataVisitor.filter((f: any) => !isEqual(f.fieldName, type));

      yield put(setVisitorName([...visitors, visitor]));
    }

    const updated = update(tripValue, type, () => data);
    const mergedtrip = merge(trip, updated);

    yield put(getNewTripSuccess(mergedtrip, updatedDocs));
    yield put(getTripFieldsSuccess({ type: type === 'vehicle' && data.type === 2 ? 'trailer' : type, data }, loaded));
  } catch (error: any) {
    loaded.isError = true;
    yield put(getTripFieldsError(loaded));
  }
}

function* getOrderWeightSaga(action: ReturnType<typeof getOrderWeightRequest>) {
  const { tripValue } = action.payload;

  try {
    const dataDocs: Document[] = yield select(dataDocsSelector);
    const trip: Trip = yield select(tripCommonTripSelector);

    const tripOrders: any = [];
    tripValue.orders.map((order: any) => tripOrders.push(`${order.number}`));

    // @ts-ignore
    const data: any = yield call(OrderService.getOrderWeight, tripOrders);

    if (data.warning === false) {
      const orders = get(tripValue, 'orders');
      orders.forEach((order: any, idx: number) => {
        order.completedDate = data.body[idx].completedDate;
        order.createdDate = data.body[idx].createdDate;
        order.netWeight = data.body[idx].fvWeight + data.body[idx].tvWeight;
        order.fvWeight = data.body[idx].fvWeight;
        order.tvWeight = data.body[idx].tvWeight;
        order.number = data.body[idx].number;
        order.positionsCount = data.body[idx].positionsCount;
      });
      // const idx = orders.findIndex((o: any) => o.number === number);
      //
      // set(tripValue, `orders[${idx}].netWeight`, `${data}`);
      // const merged = merge(orders, data);
      tripValue.orders = orders;
      yield put(getNewTripSuccess(tripValue, dataDocs));
      yield put(getOrderWeightSuccess());
    } else {
      const orders = get(tripValue, 'orders');
      set(tripValue, 'orders', orders);
      const mergedTrip = merge(trip, tripValue);
      yield put(hasWarning(data.errorMessages, data.warning, mergedTrip, dataDocs));
      yield put(getOrderWeightSuccess());
    }
  } catch (error: any) {
    yield put(getOrderWeightError(error.errorExtraParams));
    yield delay(3000);
    yield put(clearError());
  }
}

function* getOrderPartNumSaga(action: ReturnType<typeof getOrderPartNumRequest>) {
  const { orderPartNum, tripValue } = action.payload;
  const loaded = {
    type: 'number',
    isError: false,
  };

  try {
    const body = {
      orderPartNum,
      tripId: tripValue.id,
    };
    const dataDocs: Document[] = yield select(dataDocsSelector);
    const trip: Trip = yield select(tripCommonTripSelector);

    // @ts-ignore
    const data: any = yield call(OrderService.getOrderPartNum, objToFormData(body));
    if (data.warning === false) {
      const arrivedOrderPart = data?.body;
      if (arrivedOrderPart && arrivedOrderPart?.orderParts && arrivedOrderPart?.orderParts?.length > 0) {
        let doApply = false;
        tripValue?.orders?.forEach((op: any) => {
          if (op.number === arrivedOrderPart.orderParts[0].number) {
            doApply = true;
            op.orderObject = arrivedOrderPart.orderObject;
            op.company = arrivedOrderPart.company;
            op.iddp = arrivedOrderPart.company?.iddp;
            op.locationId = arrivedOrderPart.locationId;
          }
        });
        if (doApply) {
          yield put(getNewTripSuccess(tripValue, dataDocs));
          yield put(getTripOrdersSuccess());
        }
      }

      console.log(data);
    } else {
      yield put(hasWarning(data.errorMessages, data.warning, trip, dataDocs));
    }
  } catch (error: any) {
    loaded.isError = true;
    yield put(getTripFieldsError(loaded));
    yield put(getTripOrdersError(error.errorExtraParams));
    yield delay(3000);
    yield put(clearError());
  }
}

function* getTripOrdersSaga(action: ReturnType<typeof getTripOrdersRequest>) {
  const { orderObject, tripValue } = action.payload;
  const loaded = {
    type: 'number',
    isError: false,
  };
  try {
    const dataDocs: Document[] = yield select(dataDocsSelector);
    const trip: Trip = yield select(tripCommonTripSelector);
    const body = {
      orderObject,
      tripId: tripValue.id,
    };
    // @ts-ignore
    const data: any = yield call(OrderService.getTripOrders, objToFormData(body));
    if (data.warning === false) {
      // eslint-disable-next-line array-callback-return
      data.body.map((order: any) => {
        if (order.locationId === null) {
          order.locationId = '';
        }
        if (order.orderHand === null) {
          order.orderHand = '';
        }
      });
      if (trip.orders.length === 0) {
        set(tripValue, 'orders', data.body);
        const mergedTrip = merge(trip, tripValue);
        yield put(getNewTripSuccess(mergedTrip, dataDocs));
        yield put(getTripOrdersSuccess());
      } else {
        data.body.map((item: any) => trip.orders.push(item));
        const updOrders = trip.orders.map((o: any) => merge(o));
        const uniqOrders = uniqBy(updOrders, 'number');
        set(trip, 'orders', uniqOrders);
        yield put(getNewTripSuccess({ ...trip }, dataDocs));
        yield put(getTripOrdersSuccess());
      }
    } else {
      // eslint-disable-next-line array-callback-return
      data.body.map((order: any) => {
        if (order.locationId === null) {
          order.locationId = '';
        }
        if (order.orderHand === null) {
          order.orderHand = '';
        }
      });
      set(tripValue, 'orders', data.body);
      const mergedTrip = merge(trip, tripValue);
      yield put(hasWarning(data.errorMessages, data.warning, mergedTrip, dataDocs));
    }
  } catch (error: any) {
    loaded.isError = true;
    yield put(getTripFieldsError(loaded));
    yield put(getTripOrdersError(error.errorExtraParams));
    yield delay(3000);
    yield put(clearError());
  }
}

function* deleteOrderFromTripSaga(action: ReturnType<typeof deleteOrderFromTripRequest>) {
  try {
    const order = action.payload;
    const { values } = action;
    const trip: Trip = yield select(tripCommonTripSelector);
    const index = action.removeIndex;
    const body = {
      number: order.number,
      isInputError: order.isInputError,
      isDeleted: order.isDeleted,
      tripId: trip.id,
    };

    const copyTrip = cloneDeep(trip);
    const mergedTrip = merge(copyTrip, values);
    mergedTrip.orders.splice(index, 1);

    // @ts-ignore
    yield call(OrderService.deleteTripOrder, body);
    yield put(deleteOrderFromTripSuccess(mergedTrip));
  } catch (error: any) {
    yield delay(3000);
    yield put(clearError());
  }
}

export function* tripFieldsSagaWatcher() {
  yield takeLatest(GET_TRIP_FIELDS_REQUEST, getTripFieldsSaga);
  yield takeLatest(GET_ORDER_WEIGHT_REQUEST, getOrderWeightSaga);
  yield takeLatest(GET_TRIP_ORDERS_REQUEST, getTripOrdersSaga);
  yield takeLatest(GET_ORDER_PART_NUM_REQUEST, getOrderPartNumSaga);
  yield takeLatest(DELETE_ORDER_FROM_TRIP_REQUEST, deleteOrderFromTripSaga);
}
